import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Wallet, FileText, Clock, CheckCircle, LogOut, Plus, 
  CreditCard, History, Building2, User, ChevronRight, DollarSign, Download, RefreshCcw, Upload, X, Edit
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const B2BDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [creditRequests, setCreditRequests] = useState([]);
  const [agentData, setAgentData] = useState(null);
  const [myRates, setMyRates] = useState([]);

  // Resubmit sent_back application state
  const [resubmitOpen, setResubmitOpen] = useState(false);
  const [resubmitApp, setResubmitApp] = useState(null);
  const [resubmitFiles, setResubmitFiles] = useState({});
  const [resubmitResponse, setResubmitResponse] = useState('');
  const [resubmitLoading, setResubmitLoading] = useState(false);
  
  // Draft application state
  const [submitDraftLoading, setSubmitDraftLoading] = useState(null);
  const [deleteDraftLoading, setDeleteDraftLoading] = useState(null);
  
  // Cancel application state
  const [cancelLoading, setCancelLoading] = useState(null);
  
  // Edit application state
  const [editAppOpen, setEditAppOpen] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('agent_token');
    const agent = localStorage.getItem('agent_data');
    
    if (!token) {
      navigate('/b2b-agent');
      return;
    }
    
    if (agent) {
      setAgentData(JSON.parse(agent));
    }
    
    fetchDashboardData();
  }, [navigate]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('agent_token')}` }
  });

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, appsRes, transRes, creditsRes, ratesRes] = await Promise.all([
        axios.get(`${API}/b2b/dashboard`, getAuthHeader()),
        axios.get(`${API}/b2b/applications`, getAuthHeader()),
        axios.get(`${API}/b2b/wallet/transactions`, getAuthHeader()),
        axios.get(`${API}/b2b/credit-requests`, getAuthHeader()),
        axios.get(`${API}/b2b/rates`, getAuthHeader())
      ]);
      
      setStats(dashboardRes.data);
      setApplications(appsRes.data);
      setTransactions(transRes.data);
      setCreditRequests(creditsRes.data);
      
      // Convert rates object to array for display
      const ratesObj = ratesRes.data.rates || {};
      const ratesArray = Object.entries(ratesObj).map(([nationality, data]) => ({
        nationality,
        ...data.rates,  // Spread the actual rates
        done_base_rates: data.done_base_rates || {},
        supporting_1_rates: data.supporting_1_rates || {},
        supporting_2_rates: data.supporting_2_rates || {}
      }));
      setMyRates(ratesArray);
    } catch (error) {
      console.error('Dashboard error:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('agent_token');
        localStorage.removeItem('agent_data');
        navigate('/b2b-agent');
      } else {
        toast.error('Failed to load dashboard data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    // Call logout API to invalidate session on server
    const token = localStorage.getItem('agent_token');
    if (token) {
      try {
        await axios.post(
          `${API}/b2b/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        // Ignore logout API errors
        console.error('Logout API error:', error);
      }
    }
    
    localStorage.removeItem('agent_token');
    localStorage.removeItem('agent_data');
    localStorage.removeItem('agent_session_id');
    navigate('/b2b-agent');
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'sent_back': return 'bg-amber-100 text-amber-800';
      case 'draft': return 'bg-purple-100 text-purple-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const handleResubmit = async () => {
    setResubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append('agent_response', resubmitResponse);
      
      // Add any updated files
      if (resubmitFiles.passport_doc) formData.append('passport_doc', resubmitFiles.passport_doc);
      if (resubmitFiles.personal_photo_doc) formData.append('personal_photo_doc', resubmitFiles.personal_photo_doc);
      if (resubmitFiles.id_front_doc) formData.append('id_front_doc', resubmitFiles.id_front_doc);
      if (resubmitFiles.id_back_doc) formData.append('id_back_doc', resubmitFiles.id_back_doc);
      if (resubmitFiles.frc_mrc_doc) formData.append('frc_mrc_doc', resubmitFiles.frc_mrc_doc);
      if (resubmitFiles.supporting_docs) formData.append('supporting_docs', resubmitFiles.supporting_docs);
      
      await axios.post(`${API}/b2b/applications/${resubmitApp.id}/resubmit`, formData, {
        ...getAuthHeader(),
        headers: {
          ...getAuthHeader().headers,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Application resubmitted successfully!');
      setResubmitOpen(false);
      setResubmitApp(null);
      setResubmitFiles({});
      setResubmitResponse('');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit application');
    } finally {
      setResubmitLoading(false);
    }
  };

  // Submit draft application for processing
  const handleSubmitDraft = async (appId) => {
    setSubmitDraftLoading(appId);
    try {
      const response = await axios.post(`${API}/b2b/applications/${appId}/submit`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('agent_token')}` }
      });
      
      toast.success(`Application submitted! ${response.data.amount_charged > 0 ? `Charged: ${response.data.amount_charged} AED` : ''}`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitDraftLoading(null);
    }
  };

  // Delete draft application
  const handleDeleteDraft = async (appId) => {
    if (!window.confirm('Are you sure you want to delete this draft application?')) return;
    
    setDeleteDraftLoading(appId);
    try {
      await axios.delete(`${API}/b2b/applications/${appId}/draft`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('agent_token')}` }
      });
      
      toast.success('Draft deleted');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete draft');
    } finally {
      setDeleteDraftLoading(null);
    }
  };

  // Cancel application and get refund
  const handleCancelApplication = async (appId) => {
    if (!window.confirm('Are you sure you want to cancel this application? If charged, the amount will be refunded to your wallet.')) return;
    
    setCancelLoading(appId);
    try {
      const response = await axios.post(`${API}/b2b/applications/${appId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('agent_token')}` }
      });
      
      const refundMsg = response.data.refund_amount > 0 
        ? `Refunded: ${response.data.refund_amount} AED. New balance: ${response.data.new_wallet_balance} AED`
        : 'No refund (application was not charged)';
      toast.success(`Application cancelled! ${refundMsg}`);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel application');
    } finally {
      setCancelLoading(null);
    }
  };

  // Open edit dialog for application
  const handleEditApplication = (app) => {
    setEditingApp({
      ...app,
      // Ensure all fields have values
      first_name: app.first_name || '',
      middle_name: app.middle_name || '',
      last_name: app.last_name || '',
      passport_number: app.passport_number || '',
      date_of_issue: app.date_of_issue || '',
      date_of_expiry: app.date_of_expiry || '',
      date_of_birth: app.date_of_birth || '',
      occupation: app.occupation || '',
      nationality: app.nationality || '',
      visa_duration: app.visa_duration || '30_days',
      father_name: app.father_name || '',
      mother_name: app.mother_name || '',
      gender: app.gender || 'Male',
      birth_place: app.birth_place || '',
      birth_country: app.birth_country || '',
      marital_status: app.marital_status || 'Single',
      passport_issue_place: app.passport_issue_place || '',
      coming_from: app.coming_from || '',
      religion: app.religion || '',
      address: app.address || '',
      mobile: app.mobile || '',
      your_reference: app.your_reference || '',
    });
    setEditAppOpen(true);
  };

  // Save edited application
  const handleSaveEdit = async () => {
    if (!editingApp) return;
    
    setEditLoading(true);
    try {
      const formData = new FormData();
      
      // Add all text fields
      formData.append('first_name', editingApp.first_name);
      formData.append('middle_name', editingApp.middle_name);
      formData.append('last_name', editingApp.last_name);
      formData.append('passport_number', editingApp.passport_number);
      formData.append('date_of_issue', editingApp.date_of_issue);
      formData.append('date_of_expiry', editingApp.date_of_expiry);
      formData.append('date_of_birth', editingApp.date_of_birth);
      formData.append('occupation', editingApp.occupation);
      formData.append('nationality', editingApp.nationality);
      formData.append('visa_duration', editingApp.visa_duration);
      formData.append('father_name', editingApp.father_name);
      formData.append('mother_name', editingApp.mother_name);
      formData.append('gender', editingApp.gender);
      formData.append('birth_place', editingApp.birth_place);
      formData.append('birth_country', editingApp.birth_country);
      formData.append('marital_status', editingApp.marital_status);
      formData.append('passport_issue_place', editingApp.passport_issue_place);
      formData.append('coming_from', editingApp.coming_from);
      formData.append('religion', editingApp.religion);
      formData.append('address', editingApp.address);
      formData.append('mobile', editingApp.mobile);
      formData.append('your_reference', editingApp.your_reference);
      
      // Add documents if uploaded
      if (editingApp.new_passport_doc) formData.append('passport_doc', editingApp.new_passport_doc);
      if (editingApp.new_personal_photo_doc) formData.append('personal_photo_doc', editingApp.new_personal_photo_doc);
      if (editingApp.new_id_front_doc) formData.append('id_front_doc', editingApp.new_id_front_doc);
      if (editingApp.new_id_back_doc) formData.append('id_back_doc', editingApp.new_id_back_doc);
      
      await axios.put(`${API}/b2b/applications/${editingApp.id}`, formData, {
        headers: { 
          Authorization: `Bearer ${localStorage.getItem('agent_token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Application updated successfully!');
      setEditAppOpen(false);
      setEditingApp(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    } finally {
      setEditLoading(false);
    }
  };

  // Resubmit sent_back application (simple - just change status to pending)
  const handleSimpleResubmit = async (appId) => {
    try {
      await axios.post(`${API}/b2b/applications/${appId}/resubmit`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('agent_token')}` }
      });
      toast.success('Application resubmitted for review!');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit application');
    }
  };

  // Reactivate cancelled application (move to draft)
  const handleReactivateApplication = async (appId) => {
    try {
      await axios.post(`${API}/b2b/applications/${appId}/reactivate`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('agent_token')}` }
      });
      toast.success('Application reactivated! You can now edit and submit it.');
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reactivate application');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#1A4D7F] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="b2b-dashboard">
      {/* Header */}
      <header className="bg-[#1A4D7F] text-white sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Building2 className="w-8 h-8" />
              <div>
                <h1 className="text-xl font-heading font-bold">B2B Agent Portal</h1>
                <p className="text-blue-100 text-sm">{agentData?.company_name || 'Agent Dashboard'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="bg-white/10 rounded-lg px-4 py-2">
                <p className="text-xs text-blue-100">Wallet Balance</p>
                <p className="text-xl font-bold">{stats?.wallet_balance?.toFixed(2) || '0.00'} AED</p>
              </div>
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/10"
                onClick={handleLogout}
                data-testid="logout-button"
              >
                <LogOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Negative Balance Warning */}
        {stats?.wallet_balance < 0 && (
          <div className="bg-red-50 border border-red-300 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Wallet className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="font-medium text-red-800">Account Balance Negative</p>
                <p className="text-sm text-red-700">
                  Your wallet balance is <strong>{stats?.wallet_balance?.toFixed(2)} AED</strong>. 
                  Please top up to download visa documents.
                </p>
              </div>
              <Button 
                className="ml-auto bg-red-600 hover:bg-red-700"
                onClick={() => navigate('/b2b-agent/add-credit')}
              >
                Top Up Now
              </Button>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className={`border-l-4 ${stats?.wallet_balance < 0 ? 'border-l-red-500' : 'border-l-[#009FB7]'}`}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Wallet Balance</p>
                  <p className={`text-2xl font-bold ${stats?.wallet_balance < 0 ? 'text-red-600' : 'text-[#1A4D7F]'}`}>
                    {stats?.wallet_balance?.toFixed(2)} AED
                  </p>
                </div>
                <Wallet className={`w-10 h-10 ${stats?.wallet_balance < 0 ? 'text-red-500' : 'text-[#009FB7]'}`} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Applications</p>
                  <p className="text-2xl font-bold text-[#1A4D7F]">{stats?.total_applications || 0}</p>
                </div>
                <FileText className="w-10 h-10 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-[#1A4D7F]">{stats?.pending_applications || 0}</p>
                </div>
                <Clock className="w-10 h-10 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Approved</p>
                  <p className="text-2xl font-bold text-[#1A4D7F]">{stats?.approved_applications || 0}</p>
                </div>
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: <Building2 className="w-4 h-4" /> },
            { id: 'applications', label: 'Applications', icon: <FileText className="w-4 h-4" /> },
            { id: 'rates', label: 'My Rates', icon: <DollarSign className="w-4 h-4" /> },
            { id: 'wallet', label: 'Wallet & Transactions', icon: <Wallet className="w-4 h-4" /> },
            { id: 'credit', label: 'Credit Requests', icon: <CreditCard className="w-4 h-4" /> }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              className={activeTab === tab.id ? 'bg-[#1A4D7F]' : ''}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.id}`}
            >
              {tab.icon}
              <span className="ml-2">{tab.label}</span>
            </Button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button 
                  className="w-full bg-[#009FB7] hover:bg-[#008fa5] justify-between"
                  onClick={() => navigate('/b2b-agent/apply')}
                  data-testid="new-application-btn"
                >
                  <span className="flex items-center">
                    <Plus className="w-5 h-5 mr-2" />
                    New Visa Application
                  </span>
                  <ChevronRight className="w-5 h-5" />
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-between"
                  onClick={() => navigate('/b2b-agent/add-credit')}
                  data-testid="add-credit-btn"
                >
                  <span className="flex items-center">
                    <CreditCard className="w-5 h-5 mr-2" />
                    Request Credit / Upload Receipt
                  </span>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </CardContent>
            </Card>

            {/* Recent Applications */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Applications</CardTitle>
                <CardDescription>Last 5 visa applications</CardDescription>
              </CardHeader>
              <CardContent>
                {applications.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No applications yet</p>
                ) : (
                  <div className="space-y-3">
                    {applications.slice(0, 5).map(app => (
                      <div key={app.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{app.first_name} {app.last_name}</p>
                          <p className="text-sm text-slate-500">{app.passport_number}</p>
                        </div>
                        <Badge className={getStatusColor(app.status)}>
                          {app.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Applications Tab */}
        {activeTab === 'applications' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>All Applications</CardTitle>
                <CardDescription>Manage your visa applications</CardDescription>
              </div>
              <Button 
                className="bg-[#009FB7]"
                onClick={() => navigate('/b2b-agent/apply')}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Application
              </Button>
            </CardHeader>
            <CardContent>
              {applications.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No applications found</p>
                  <Button 
                    className="mt-4 bg-[#1A4D7F]"
                    onClick={() => navigate('/b2b-agent/apply')}
                  >
                    Create Your First Application
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3 px-2">Applicant</th>
                        <th className="text-left py-3 px-2">Passport</th>
                        <th className="text-left py-3 px-2">Visa Type</th>
                        <th className="text-left py-3 px-2">Type</th>
                        <th className="text-left py-3 px-2">Fee</th>
                        <th className="text-left py-3 px-2">Status</th>
                        <th className="text-left py-3 px-2">Date</th>
                        <th className="text-left py-3 px-2">Visa</th>
                      </tr>
                    </thead>
                    <tbody>
                      {applications.map(app => (
                        <tr key={app.id} className="border-b hover:bg-slate-50">
                          <td className="py-3 px-2">
                            <div>
                              <p className="font-medium">{app.first_name} {app.last_name}</p>
                              <p className="text-sm text-slate-500">{app.nationality}</p>
                            </div>
                          </td>
                          <td className="py-3 px-2">{app.passport_number}</td>
                          <td className="py-3 px-2">
                            <Badge variant="outline">
                              {app.visa_duration === '30_days' ? '30 Days' : 
                               app.visa_duration === '60_days' ? '60 Days' : '1M Extension'}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            {app.is_done_base ? (
                              <Badge className="bg-orange-100 text-orange-800 text-xs">Done Base</Badge>
                            ) : (
                              <Badge className="bg-blue-100 text-blue-800 text-xs">Normal</Badge>
                            )}
                          </td>
                          <td className="py-3 px-2">{app.total_visa_fee} AED</td>
                          <td className="py-3 px-2">
                            <Badge className={getStatusColor(app.status)}>
                              {app.status === 'sent_back' ? 'Needs Correction' : 
                               app.status === 'draft' ? 'Draft (Saved)' : app.status}
                            </Badge>
                            {app.status === 'sent_back' && app.admin_notes && (
                              <p className="text-xs text-amber-600 mt-1 max-w-[150px] truncate" title={app.admin_notes}>
                                {app.admin_notes}
                              </p>
                            )}
                            {app.status === 'draft' && (
                              <p className="text-xs text-blue-600 mt-1">Not charged yet</p>
                            )}
                          </td>
                          <td className="py-3 px-2 text-sm text-slate-500">
                            {new Date(app.created_at).toLocaleDateString()}
                          </td>
                          <td className="py-3 px-2">
                            {app.status === 'draft' ? (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-blue-500 text-blue-600 hover:bg-blue-50"
                                  onClick={() => handleEditApplication(app)}
                                  data-testid={`edit-draft-${app.id}`}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => handleSubmitDraft(app.id)}
                                  disabled={submitDraftLoading === app.id}
                                  data-testid={`submit-draft-${app.id}`}
                                >
                                  {submitDraftLoading === app.id ? (
                                    <span className="animate-spin">⏳</span>
                                  ) : (
                                    <>
                                      <CheckCircle className="w-3 h-3 mr-1" />
                                      Submit
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-red-400 text-red-600 hover:bg-red-50"
                                  onClick={() => handleDeleteDraft(app.id)}
                                  disabled={deleteDraftLoading === app.id}
                                  data-testid={`delete-draft-${app.id}`}
                                >
                                  {deleteDraftLoading === app.id ? (
                                    <span className="animate-spin">⏳</span>
                                  ) : (
                                    <X className="w-3 h-3" />
                                  )}
                                </Button>
                              </div>
                            ) : app.status === 'approved' && app.visa_document_url ? (
                              stats?.wallet_balance < 0 ? (
                                <span className="text-xs text-red-600">Top up to download</span>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                                  onClick={() => window.open(`${BACKEND_URL}${app.visa_document_url}`, '_blank')}
                                  data-testid={`download-visa-${app.id}`}
                                >
                                  <Download className="w-3 h-3 mr-1" />
                                  Download
                                </Button>
                              )
                            ) : app.status === 'approved' ? (
                              <span className="text-xs text-slate-400">Processing...</span>
                            ) : app.status === 'sent_back' ? (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-blue-500 text-blue-600 hover:bg-blue-50"
                                  onClick={() => handleEditApplication(app)}
                                  data-testid={`edit-app-${app.id}`}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-amber-500 text-amber-600 hover:bg-amber-50"
                                  onClick={() => handleSimpleResubmit(app.id)}
                                  data-testid={`resubmit-${app.id}`}
                                >
                                  <RefreshCcw className="w-3 h-3 mr-1" />
                                  Resubmit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-red-400 text-red-600 hover:bg-red-50"
                                  onClick={() => handleCancelApplication(app.id)}
                                  disabled={cancelLoading === app.id}
                                  data-testid={`cancel-app-${app.id}`}
                                >
                                  {cancelLoading === app.id ? (
                                    <span className="animate-spin">⏳</span>
                                  ) : (
                                    'Cancel'
                                  )}
                                </Button>
                              </div>
                            ) : app.status === 'cancelled' ? (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-blue-500 text-blue-600 hover:bg-blue-50"
                                  onClick={() => handleEditApplication(app)}
                                  data-testid={`edit-cancelled-${app.id}`}
                                >
                                  Edit
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-green-500 text-green-600 hover:bg-green-50"
                                  onClick={() => handleReactivateApplication(app.id)}
                                  data-testid={`reactivate-${app.id}`}
                                >
                                  <RefreshCcw className="w-3 h-3 mr-1" />
                                  Reactivate
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* My Rates Tab */}
        {activeTab === 'rates' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl font-heading">My Visa Rates</CardTitle>
              <CardDescription>
                These are the rates configured for your account. Contact admin to add more nationalities.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {myRates.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-lg">
                  <DollarSign className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">No rates configured</p>
                  <p className="text-slate-500 text-sm mt-1">
                    Contact your admin or sales representative to configure rates for your account.
                  </p>
                  <p className="text-sm text-slate-400 mt-3">
                    Email: <a href="mailto:sales@aonevisas.com" className="text-[#1A4D7F] underline">sales@aonevisas.com</a>
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Regular Rates */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3">Regular Rates</h3>
                    <div className="rounded-lg border border-slate-200 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead>Nationality</TableHead>
                            <TableHead className="text-right">30 Days (AED)</TableHead>
                            <TableHead className="text-right">60 Days (AED)</TableHead>
                            <TableHead className="text-right">1M Extension (AED)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {myRates.map((rate) => (
                            <TableRow key={rate.nationality} className="hover:bg-slate-50">
                              <TableCell className="font-medium">{rate.nationality}</TableCell>
                              <TableCell className="text-right">{rate['30_days']?.toFixed(2) || '-'}</TableCell>
                              <TableCell className="text-right">{rate['60_days']?.toFixed(2) || '-'}</TableCell>
                              <TableCell className="text-right">{rate['1m_extension']?.toFixed(2) || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  {/* Done Base - Family Application Rates */}
                  {myRates.some(r => r.done_base_rates && Object.keys(r.done_base_rates).length > 0 && r.done_base_rates['30_days'] > 0) && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-orange-700">Done Base - Family Application</h3>
                      <p className="text-xs text-slate-500 mb-2">When family members also apply for visa. Pay on Approval.</p>
                      <div className="rounded-lg border border-orange-200 overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-orange-50">
                              <TableHead>Nationality</TableHead>
                              <TableHead className="text-right">30 Days (AED)</TableHead>
                              <TableHead className="text-right">60 Days (AED)</TableHead>
                              <TableHead className="text-right">1M Extension (AED)</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myRates.filter(r => r.done_base_rates && r.done_base_rates['30_days'] > 0).map((rate) => (
                              <TableRow key={`db-${rate.nationality}`} className="hover:bg-orange-50">
                                <TableCell className="font-medium">{rate.nationality}</TableCell>
                                <TableCell className="text-right">{rate.done_base_rates['30_days']?.toFixed(2) || '-'}</TableCell>
                                <TableCell className="text-right">{rate.done_base_rates['60_days']?.toFixed(2) || '-'}</TableCell>
                                <TableCell className="text-right">{rate.done_base_rates['1m_extension']?.toFixed(2) || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* Done Base - With 1 Supporting Person */}
                  {myRates.some(r => r.supporting_1_rates && r.supporting_1_rates['30_days'] > 0) && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-blue-700">Done Base - 1 Supporting Person</h3>
                      <p className="text-xs text-slate-500 mb-2">Applicant with docs from 1 family member (mother/father/sister/wife). Pay on Approval.</p>
                      <div className="rounded-lg border border-blue-200 overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-blue-50">
                              <TableHead>Nationality</TableHead>
                              <TableHead className="text-right">30 Days (AED)</TableHead>
                              <TableHead className="text-right">60 Days (AED)</TableHead>
                              <TableHead className="text-right">1M Extension (AED)</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myRates.filter(r => r.supporting_1_rates && r.supporting_1_rates['30_days'] > 0).map((rate) => (
                              <TableRow key={`s1-${rate.nationality}`} className="hover:bg-blue-50">
                                <TableCell className="font-medium">{rate.nationality}</TableCell>
                                <TableCell className="text-right">{rate.supporting_1_rates['30_days']?.toFixed(2) || '-'}</TableCell>
                                <TableCell className="text-right">{rate.supporting_1_rates['60_days']?.toFixed(2) || '-'}</TableCell>
                                <TableCell className="text-right">{rate.supporting_1_rates['1m_extension']?.toFixed(2) || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* Done Base - With 2 Supporting Persons */}
                  {myRates.some(r => r.supporting_2_rates && r.supporting_2_rates['30_days'] > 0) && (
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-purple-700">Done Base - 2 Supporting Persons</h3>
                      <p className="text-xs text-slate-500 mb-2">Applicant with docs from 2 family members (e.g., mother + father). Pay on Approval.</p>
                      <div className="rounded-lg border border-purple-200 overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-purple-50">
                              <TableHead>Nationality</TableHead>
                              <TableHead className="text-right">30 Days (AED)</TableHead>
                              <TableHead className="text-right">60 Days (AED)</TableHead>
                              <TableHead className="text-right">1M Extension (AED)</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {myRates.filter(r => r.supporting_2_rates && r.supporting_2_rates['30_days'] > 0).map((rate) => (
                              <TableRow key={`s2-${rate.nationality}`} className="hover:bg-purple-50">
                                <TableCell className="font-medium">{rate.nationality}</TableCell>
                                <TableCell className="text-right">{rate.supporting_2_rates['30_days']?.toFixed(2) || '-'}</TableCell>
                                <TableCell className="text-right">{rate.supporting_2_rates['60_days']?.toFixed(2) || '-'}</TableCell>
                                <TableCell className="text-right">{rate.supporting_2_rates['1m_extension']?.toFixed(2) || '-'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> You can only submit visa applications for nationalities listed above. 
                  To add more nationalities or request rate changes, please contact your admin.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Wallet Tab */}
        {activeTab === 'wallet' && (
          <div className="space-y-6">
            <Card className="bg-gradient-to-r from-[#1A4D7F] to-[#009FB7] text-white">
              <CardContent className="p-8">
                <p className="text-blue-100 mb-2">Available Balance</p>
                <p className="text-4xl font-bold mb-4">{stats?.wallet_balance?.toFixed(2)} AED</p>
                <Button 
                  className="bg-white text-[#1A4D7F] hover:bg-blue-50"
                  onClick={() => navigate('/b2b-agent/add-credit')}
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  Add Credit
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Transaction History
                </CardTitle>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No transactions yet</p>
                ) : (
                  <div className="space-y-3">
                    {transactions.map(txn => (
                      <div key={txn.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{txn.description}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(txn.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.type === 'credit' ? '+' : '-'}{txn.amount.toFixed(2)} AED
                          </p>
                          <p className="text-sm text-slate-500">Balance: {txn.balance_after.toFixed(2)} AED</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Credit Requests Tab */}
        {activeTab === 'credit' && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Credit Requests</CardTitle>
                <CardDescription>Your payment receipts and credit requests</CardDescription>
              </div>
              <Button 
                className="bg-[#009FB7]"
                onClick={() => navigate('/b2b-agent/add-credit')}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Request
              </Button>
            </CardHeader>
            <CardContent>
              {creditRequests.length === 0 ? (
                <div className="text-center py-12">
                  <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No credit requests yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {creditRequests.map(req => (
                    <div key={req.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{req.amount.toFixed(2)} AED</p>
                        <p className="text-sm text-slate-500">{req.notes || 'No notes'}</p>
                        <p className="text-xs text-slate-400">
                          {new Date(req.created_at).toLocaleString()}
                        </p>
                      </div>
                      <Badge className={
                        req.status === 'approved' ? 'bg-green-100 text-green-800' :
                        req.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>
                        {req.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Resubmit Application Dialog */}
      <Dialog open={resubmitOpen} onOpenChange={setResubmitOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-amber-600">Resubmit Application</DialogTitle>
            <DialogDescription>
              Review the admin's note and upload corrected documents if needed.
            </DialogDescription>
          </DialogHeader>
          {resubmitApp && (
            <div className="space-y-4">
              {/* Admin Note */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 font-medium mb-1">Admin's Note:</p>
                <p className="text-amber-900">{resubmitApp.admin_notes || 'No specific note provided'}</p>
              </div>

              {/* Application Info */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-600">Applicant: <strong>{resubmitApp.first_name} {resubmitApp.last_name}</strong></p>
                <p className="text-sm text-slate-600">Passport: <strong>{resubmitApp.passport_number}</strong></p>
              </div>

              {/* Response to Admin */}
              <div>
                <Label>Your Response (Optional)</Label>
                <Textarea
                  value={resubmitResponse}
                  onChange={(e) => setResubmitResponse(e.target.value)}
                  placeholder="Explain what you've corrected..."
                  className="mt-1"
                  rows={2}
                />
              </div>

              {/* Document Updates */}
              <div className="space-y-3">
                <Label className="text-slate-700">Update Documents (Optional - only upload if replacing)</Label>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'passport_doc', label: 'Passport' },
                    { key: 'personal_photo_doc', label: 'Personal Photo' },
                    { key: 'id_front_doc', label: 'ID Card Front' },
                    { key: 'id_back_doc', label: 'ID Card Back' },
                    { key: 'frc_mrc_doc', label: 'FRC/MRC' },
                    { key: 'supporting_docs', label: 'Supporting Docs' }
                  ].map(doc => (
                    <div key={doc.key} className="space-y-1">
                      <Label className="text-xs text-slate-500">{doc.label}</Label>
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setResubmitFiles(prev => ({
                          ...prev,
                          [doc.key]: e.target.files[0]
                        }))}
                        className="text-xs"
                      />
                      {resubmitFiles[doc.key] && (
                        <p className="text-xs text-green-600">{resubmitFiles[doc.key].name}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setResubmitOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleResubmit}
              disabled={resubmitLoading}
              className="bg-amber-500 hover:bg-amber-600"
              data-testid="confirm-resubmit-btn"
            >
              {resubmitLoading ? 'Resubmitting...' : 'Resubmit Application'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Application Dialog */}
      <Dialog open={editAppOpen} onOpenChange={setEditAppOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Application</DialogTitle>
            <DialogDescription>
              Update application details. Status: {editingApp?.status}
            </DialogDescription>
          </DialogHeader>
          {editingApp && (
            <div className="space-y-4">
              {/* Personal Info */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">First Name *</Label>
                  <Input
                    value={editingApp.first_name}
                    onChange={(e) => setEditingApp({...editingApp, first_name: e.target.value.toUpperCase()})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Middle Name</Label>
                  <Input
                    value={editingApp.middle_name}
                    onChange={(e) => setEditingApp({...editingApp, middle_name: e.target.value.toUpperCase()})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Last Name *</Label>
                  <Input
                    value={editingApp.last_name}
                    onChange={(e) => setEditingApp({...editingApp, last_name: e.target.value.toUpperCase()})}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Passport Info */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Passport Number *</Label>
                  <Input
                    value={editingApp.passport_number}
                    onChange={(e) => setEditingApp({...editingApp, passport_number: e.target.value.toUpperCase()})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Date of Issue *</Label>
                  <Input
                    type="date"
                    value={editingApp.date_of_issue}
                    onChange={(e) => setEditingApp({...editingApp, date_of_issue: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Date of Expiry *</Label>
                  <Input
                    type="date"
                    value={editingApp.date_of_expiry}
                    onChange={(e) => setEditingApp({...editingApp, date_of_expiry: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Other Info */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Date of Birth *</Label>
                  <Input
                    type="date"
                    value={editingApp.date_of_birth}
                    onChange={(e) => setEditingApp({...editingApp, date_of_birth: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Nationality *</Label>
                  <Input
                    value={editingApp.nationality}
                    onChange={(e) => setEditingApp({...editingApp, nationality: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Occupation *</Label>
                  <Input
                    value={editingApp.occupation}
                    onChange={(e) => setEditingApp({...editingApp, occupation: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Father Name</Label>
                  <Input
                    value={editingApp.father_name}
                    onChange={(e) => setEditingApp({...editingApp, father_name: e.target.value.toUpperCase()})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Mother Name</Label>
                  <Input
                    value={editingApp.mother_name}
                    onChange={(e) => setEditingApp({...editingApp, mother_name: e.target.value.toUpperCase()})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Mobile</Label>
                  <Input
                    value={editingApp.mobile}
                    onChange={(e) => setEditingApp({...editingApp, mobile: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Address</Label>
                  <Input
                    value={editingApp.address}
                    onChange={(e) => setEditingApp({...editingApp, address: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs">Your Reference</Label>
                  <Input
                    value={editingApp.your_reference}
                    onChange={(e) => setEditingApp({...editingApp, your_reference: e.target.value})}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Document Updates */}
              <div className="border-t pt-4">
                <Label className="text-sm font-medium">Update Documents (Optional - only upload if replacing)</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  {[
                    { key: 'new_passport_doc', label: 'Passport', current: editingApp.passport_doc_url },
                    { key: 'new_personal_photo_doc', label: 'Personal Photo', current: editingApp.personal_photo_url },
                    { key: 'new_id_front_doc', label: 'ID Front', current: editingApp.id_front_url },
                    { key: 'new_id_back_doc', label: 'ID Back', current: editingApp.id_back_url },
                  ].map(doc => (
                    <div key={doc.key} className="space-y-1">
                      <Label className="text-xs text-slate-500">
                        {doc.label} {doc.current && <span className="text-green-600">(uploaded)</span>}
                      </Label>
                      <Input
                        type="file"
                        accept="image/*,.pdf"
                        onChange={(e) => setEditingApp(prev => ({
                          ...prev,
                          [doc.key]: e.target.files[0]
                        }))}
                        className="text-xs"
                      />
                      {editingApp[doc.key] && (
                        <p className="text-xs text-green-600">{editingApp[doc.key].name}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAppOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveEdit}
              disabled={editLoading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="save-edit-btn"
            >
              {editLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default B2BDashboard;
